/**
 * The appointment wizard's data ports -- the one place the Appointments designer
 * preview and the standalone public booking page differ.
 *
 * They render through the SAME component and the same controller. What changes
 * is only where the settings, the catalogue and the slots come from, and whether
 * a submit is real:
 *
 *   preview  draft settings from the designer's own form; catalogue from the
 *            journey it is editing; availability from the REAL public endpoint;
 *            no booking is ever created.
 *   public   published settings for a slug; catalogue from the same published
 *            config; the same availability endpoint; a real booking.
 *
 * Everything here goes through the public transport. Anonymous visitors use
 * the established public booking operation; a delegated portal visitor uses
 * the same-origin booking-session context and booking routes so the host-only
 * cookies never leave the booking origin and profile data never enters a URL.
 *
 * Nothing in this module invents data. Where a read returns nothing the caller
 * gets an empty result and shows an empty state -- never a placeholder row. The
 * wizard used to fall back to a baked fixture-tenant catalogue and to generate slots
 * by arithmetic on the calendar date; on a customer-facing page that is
 * fabricated content, and it is the reason this module exists.
 */
import {
  bookPublicJourneyAppointment,
  bookPortalJourneyAppointment,
  getPortalBookingCustomerContext,
  getPublicJourneyAvailability,
  previewAdminJourneyAvailability,
  requestPublicSurface,
} from "@v0y4ge/api-client";
import { studioRequestOptions } from "../../lib/studio-config";
import type { PublicJourneyAvailabilityQuery } from "@v0y4ge/api-contracts";
import { publishedServicesOnly } from "./appointment-service-filters";
import { publicAttributionForSubmit } from "../../lib/public-touch";

export type AppointmentMode = "preview" | "public";

export interface PublicSlot {
  id: string;
  time: string;
  startUtc: string;
  endUtc: string;
  staffId: string;
  staffName: string | null;
  /** The STORE this slot belongs to, when the producer names one.
   *
   *  Store-level availability makes a slot identifiable by instant + store, not
   *  by instant alone: the service-wide window (no `locationId` asked for) is
   *  a union across every store offering the service, so two stores can offer
   *  the same `startUtc` under the same displayed label. Submitting
   *  `state.loc` in that case books whichever store the step happened to be
   *  pointing at, which is only correct when the visitor explicitly chose one.
   *
   *  Optional because a producer without a store dimension omits it; callers
   *  fall back to the chosen store only when it is absent. */
  locationId?: string;
  /** Capacity left at this offer, exactly as the producer sent it. Kept only
   *  when it is a finite number; never defaulted, never parsed from a string.
   *  A Where-step location is eligible only through an offer with `remaining > 0`. */
  remaining?: number;
}

/** One booking question as the forms creator authored it, resolved server-side
 *  from the service's question group. Rendered at the details step. */
export interface BookingQuestion {
  id: string; label: string; type: string; help: string; required: boolean;
  options: { id: string; label: string }[]; groupId: string | null; groupName: string | null;
}

export interface AppointmentCatalog {
  /** `locs` is present ONLY when a restriction is explicitly configured for the
   *  service; absent means none is, and availability alone decides where it is offered. */
  /** The service's persisted image, carried THROUGH from the catalogue row —
   *  never derived. "" when the row has none, which is the consumer's signal to
   *  fall back rather than draw a placeholder. */
  services: { id: string; name: string; desc: string; cat: string; dur: string; price: string; window: number; locs?: string[]; image: string; questions: BookingQuestion[]; method: string }[];
  /** `latitude`/`longitude` are carried THROUGH, not derived. They arrive on
   *  the tenant's location rows (platform PR #1888 / map-sync 07c1ae419) and
   *  the map renders real pins from them. The projection used to drop the pair,
   *  which left the journey's map with nothing to place and is why it fell back
   *  to fabricated percentage positions. Optional: a location with no located
   *  pin has none, and must not be given one. */
  locations: { id: string; name: string; addr: string; dist: string; tz: string; next: string; hours: string; phone: string; virtual?: boolean; latitude?: number; longitude?: number }[];
  staff: { id: string; name: string }[];
  durations: { id: string; label: string; sub: string }[];
}

export const EMPTY_CATALOG: AppointmentCatalog = { services: [], locations: [], staff: [], durations: [] };

/**
 * Where an explicit-base public read points.
 *
 * Studio is served from a host that injects its own configured API base, and it
 * already carries the tenant prefix -- studioRequestOptions() is that base and
 * is the only supported way to reach it. The standalone booking bundle is served
 * same-origin with its API through the gateway, so an empty base plus host
 * tenant routing is correct there.
 *
 * Deliberately NOT a call to configureApiClient. The shared component runs
 * inside Studio, and reconfiguring the global client from it would repoint every
 * other screen's requests.
 */
export function publicReadOptions(mode: AppointmentMode): { baseUrl: string } {
  return mode === "public" ? { baseUrl: "" } : { baseUrl: studioRequestOptions().baseUrl };
}

/**
 * The member a personal booking link names (CC 0308 §3, 0323): `/<journey>?staff=<link name>`.
 * Read from the page address on the public page only, never in Studio. Every request for that
 * journey carries it: the journey read, catalogue read, availability, and both bookings, public
 * and signed-in (CC 0333 reverses 0323 for the signed-in one). The journey read answers with
 * personalLink. On a locked link (allowOtherOnPBL false) the times and the booking are that
 * member's; on an open link they come from the whole pool and the member is recorded on the
 * booking. A name that matches nobody answers 404 STAFF_NOT_FOUND (the page then shows its
 * not-found state, never the whole journey). The page never rewrites its address, so a reload keeps the parameter.
 */
export function personalLinkStaff(mode: AppointmentMode = "public"): string {
  if (mode !== "public" || typeof window === "undefined") return "";
  try { return (new URLSearchParams(window.location.search).get("staff") || "").trim(); } catch { return ""; }
}
const staffQuery = (mode: AppointmentMode): string => {
  const s = personalLinkStaff(mode);
  return s ? "?staff=" + encodeURIComponent(s) : "";
};

/** The slug a standalone booking page is serving, from the first path segment. */
export function slugFromPathname(pathname: string): string {
  const parts = String(pathname || "").split("/").filter(Boolean);
  return parts.length > 0 ? decodeURIComponent(parts[0]) : "";
}

type Dict = Record<string, any>;

/**
 * A location row's coordinates as ONE complete pair, or nothing.
 *
 * Two real spellings reach the catalogue projections. The admin location row
 * carries `latitude`/`longitude`; the public catalogue — the tenant's own
 * locations and a journey's published branches alike — carries `lat`/`lng`.
 * Reading only the long names silently dropped every public pin, so the map
 * reported "no coordinates" for rows that had them.
 *
 * A pair is used only when BOTH halves are finite numbers of the SAME
 * spelling. `0` and negative values are real positions and are kept. A half
 * is never paired with the other spelling's half, never defaulted to 0,
 * never parsed from a string, and never looked up: no complete pair, no
 * coordinates. The result is always the internal `latitude`/`longitude` shape.
 */
export function coordinatePairOf(row: unknown): { latitude: number; longitude: number } | { latitude?: never; longitude?: never } {
  const r = (row && typeof row === "object" ? row : {}) as Record<string, unknown>;
  const finite = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
  const latitude = r.latitude, longitude = r.longitude;
  if (finite(latitude) && finite(longitude)) return { latitude, longitude };
  const lat = r.lat, lng = r.lng;
  if (finite(lat) && finite(lng)) return { latitude: lat, longitude: lng };
  return {};
}

/**
 * The published surface for a slug: configJson, status, isLive, slug and the
 * rest. Returns null when the journey does not exist or is not published -- the
 * caller renders a content-free not-found rather than anything naming the
 * tenant.
 */
export async function loadPublishedJourney(slug: string, mode: AppointmentMode): Promise<Dict | null> {
  if (!slug) return null;
  const surface = await requestPublicSurface<Dict>(
    "/v1/journeys/public/" + encodeURIComponent(slug) + staffQuery(mode),
    { method: "GET" },
    publicReadOptions(mode),
    true,
  );
  return surface ?? null;
}

/** A storable image address: http(s) or root-relative. Never `blob:` / `data:`.
 *  Same rule as alljourneys-catalog.ts's durableImageUrl; restated because this
 *  module must stay free of the Services store's import-time hydrate. */
const durableImageUrl = (value: unknown): string => {
  const v = typeof value === "string" ? value.trim() : "";
  return v && (/^https?:\/\//i.test(v) || /^\/(?!\/)/.test(v)) ? v : "";
};

/** The persisted image on a catalogue row: the row's own `image`, else its
 *  landing hero's — the precedence every other service surface applies. */
function rowImage(row: Dict): string {
  const landing = row.landing as Dict | undefined;
  const hero = landing && typeof landing === "object" ? (landing.hero as Dict | undefined) : undefined;
  return durableImageUrl(row.image) || durableImageUrl(hero && hero.image);
}

function minutesLabel(minutes: unknown): string {
  const n = Math.round(Number(minutes) || 0);
  if (!n) return "";
  if (n < 60) return n + " min";
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m ? h + " hour " + m + " minutes" : h + " hour" + (h > 1 ? "s" : "");
}

/**
 * MAJOR units in, formatted string out.
 *
 * The unit is in the parameter name because getting it wrong is invisible: the
 * public catalogue publishes `price` ALREADY in major units -- journey-runtime's
 * catalog.ts priceField() divides the stored minor value by 100 before it is
 * serialised -- and publishes no `priceMinor` at all. Only the admin shape
 * carries minor units. Formatting both through one minor-unit helper rendered
 * every public price at a hundredth of its value, so a $50.00 service read
 * $0.50 on the customer's booking page.
 *
 * The sibling formatter in alljourneys-catalog.ts takes major units too; these
 * two must not disagree about the unit again.
 */
function priceLabel(major: unknown, currency: unknown): string {
  const n = Number(major);
  if (!Number.isFinite(n) || n <= 0) return "";
  const code = String(currency || "USD");
  const amount = n.toFixed(2);
  return code === "USD" ? "$" + amount : amount + " " + code;
}

/**
 * The catalogue the wizard draws, taken from the journey's OWN published config.
 *
 * configJson.custom carries the tenant's real hydrated services, locations and
 * staff -- the same rows the designer's admin reads return -- so one published
 * read serves both surfaces and they cannot disagree. The public /catalog
 * endpoint is deliberately not used for locations: it returns none, and the
 * branches a journey offers are a property of the journey's own config.
 */
/**
 * The catalogue a public visitor can actually be shown.
 *
 * NOT built from configJson.custom. The admin journey row carries hydrated
 * services, locations and staff there, and it is tempting to reuse -- but the
 * public surface runs the stored config through normalizeJourneyConfig, which
 * keeps only declared contract fields, so `custom` is absent from every public
 * response. Verified against the live tenant: the public payload has no custom
 * block at all, while the admin row has three services, two locations and two
 * staff in it.
 *
 * So each part comes from where the contract actually publishes it:
 *   services   the public /catalog read, narrowed by the journey's
 *              `steps.service.serviceIds` exact-id allow-list
 *   staff      the journey's own published staff roster
 *   locations  the journey's published branches
 *
 *   locations  the public /catalog read, already resolved by the server:
 *              { id, name, address?, timezone?, virtual }, with mode and
 *              pinVirtual applied server-side. The list is FINAL -- do not
 *              re-filter it here, or the two surfaces stop agreeing.
 *
 * Distance and next-available are absent by agreement: both need a live
 * calculation the catalogue cannot do, so they stay empty rather than being
 * filled with something plausible.
 */
export function buildCatalog(
  config: Dict | null | undefined,
  catalogServices: Dict[],
  catalogLocations: Dict[],
  availabilityStaff: { id: string; name: string }[],
): AppointmentCatalog {
  const publishedStaff = (config && (config as Dict).staff) || {};
  // The server's final list. This used to read the journey's own
  // `steps.location.branches`, which is empty on a journey whose branches were
  // never published -- exactly the live case -- while the server-rendered page
  // fell back to the tenant's locations and showed two. Reading the resolved
  // list is what closes that gap rather than reproducing the fallback here.
  const rawLocations: Dict[] = Array.isArray(catalogLocations) ? catalogLocations : [];
  // Published services only. The public catalogue carries no `status` -- the
  // server already filters it -- so this drops only a row that explicitly names
  // another status. Applied before the durations below are derived, so a draft's
  // lengths cannot appear either.
  const rawServices: Dict[] = publishedServicesOnly(Array.isArray(catalogServices) ? catalogServices : [], "public");
  const configuredServiceIds = Array.isArray(config?.steps?.service?.serviceIds)
    ? config.steps.service.serviceIds.map((id: unknown) => String(id).trim()).filter(Boolean)
    : [];
  const allowedServiceIds = new Set(configuredServiceIds);
  const configuredStaffIds = Array.isArray(publishedStaff.selected)
    ? publishedStaff.selected.map((id: unknown) => String(id).trim()).filter(Boolean)
    : [];
  const allowedStaffIds = new Set(configuredStaffIds);
  const rosterStaff: Dict[] = Array.isArray(publishedStaff.roster) ? publishedStaff.roster : [];
  const rawStaff: Dict[] = rosterStaff.length ? rosterStaff : availabilityStaff;

  const locations = rawLocations
    .filter((row) => row && row.id)
    .map((row) => ({
      id: String(row.id),
      // `name`, matching the tenant-row spelling the payload uses. A service is
      // the one that carries `title`.
      name: String(row.name || ""),
      // ONE composed line, composed server-side by the same helpers the booking
      // confirmation uses. Re-composing it here from parts is how the card a
      // visitor picks and the confirmation they receive end up disagreeing about
      // one place.
      //
      // A virtual branch is labelled from the FLAG, never from an empty address:
      // an addressless branch arrives as the literal "GB", because the platform's
      // address normaliser defaults country to GB and composes from the parts. A
      // fallback keyed on emptiness would never fire, and the card would print
      // "GB" as the venue.
      addr: row.virtual === true ? "Virtual appointment" : String(row.address || ""),
      // Distance needs the visitor's position, and the next free slot needs an
      // availability read per branch. Neither is known here, and inventing
      // either is what this module exists to stop.
      dist: "",
      tz: String(row.timezone || ""),
      next: "",
      hours: String(row.hours || ""),
      phone: String(row.phone || ""),
      // The catalogue's own virtual flag, carried so a virtual appointment is
      // identified by it rather than by an address, a distance or an id.
      virtual: row.virtual === true,
      // Carried verbatim as one complete pair when the row has one; omitted,
      // never zeroed, when it does not. `0, 0` is a real place in the Gulf of
      // Guinea. The public catalogue spells the pair `lat`/`lng`.
      ...coordinatePairOf(row),
    }));

  // An EXPLICIT line-of-business restriction, or none. This used to fall back to
  // every location id whenever nothing matched, which made the restriction look
  // configured when it was not and let a mismatch widen to the whole tenant.
  // A location's lines are CONFIGURED only when it names at least one: an absent
  // or EMPTY `lobs` list is unspecified (the public catalogue emits `lobs: []`
  // for a location with no branch mapping), and unspecified offers everything.
  // So: no service lob, or no location naming any line, means no restriction
  // (undefined) and the service's real offered slots decide. Otherwise the
  // restriction is the locations naming the lob plus the unspecified ones --
  // possibly empty, which is an honest "configured nowhere".
  const locsFor = (service: Dict): string[] | undefined => {
    const lob = String(service.lob || service.category || "");
    if (!lob) return undefined;
    const rows = rawLocations.filter((row) => row && row.id);
    const configured = (row: Dict) => Array.isArray(row.lobs) && row.lobs.length > 0;
    if (!rows.some(configured)) return undefined;
    return rows
      .filter((row) => !configured(row) || row.lobs.indexOf(lob) > -1)
      .map((row) => String(row.id));
  };

  const services = rawServices
    .filter((row) => row && row.id && (allowedServiceIds.size === 0 || allowedServiceIds.has(String(row.id))))
    .map((row) => ({
      id: String(row.id),
      // The public catalogue names a service `title`; the admin row calls the
      // same field `name`.
      name: String(row.title || row.name || ""),
      desc: String(row.description || ""),
      // The catalogue's display name for the category first; `category` / `lob`
      // remain as the id-shaped fallbacks an older worker publishes.
      cat: String(row.categoryName || row.category || row.lob || "").trim(),
      // `detail` is already a rendered duration ("30 min") on the public
      // catalogue; only the admin shape carries raw minutes.
      dur: String(row.detail || "") || minutesLabel(row.duration),
      // Both shapes normalised to MAJOR units before formatting: the admin row
      // carries minor units under `priceMinor`, the public catalogue carries major
      // units under `price`.
      price: priceLabel(row.priceMinor != null ? Number(row.priceMinor) / 100 : row.price, row.currency),
      // The config carries no per-service booking window; the availability read
      // is the authority on what is bookable, so this only bounds the calendar.
      window: 60,
      // The persisted image, verbatim from the catalogue row — "" when it has
      // none, so the chooser falls back instead of being handed a placeholder.
      image: rowImage(row),
      ...(() => { const restriction = locsFor(row); return restriction ? { locs: restriction } : {}; })(),
      // The service's booking questions, resolved by the catalogue read (seed
      // review step 06). Absent on an older worker → no extra fields, never invented.
      questions: Array.isArray(row.bookingQuestions) ? (row.bookingQuestions as BookingQuestion[]) : [],
      // The catalog's delivery method ("virtual", "in-person", …) verbatim; "" when absent (CC 0039).
      method: String(row.method || ""),
    }));

  const staff = rawStaff
    .filter((row) => row && row.id && (allowedStaffIds.size === 0 || allowedStaffIds.has(String(row.id))))
    .map((row) => ({ id: String(row.id), name: String(row.name || "") }));

  const durationLabels: string[] = [];
  for (const row of rawServices) {
    for (const entry of (Array.isArray(row.durations) ? row.durations : [])) {
      const label = String((entry && entry.duration) || "");
      if (label && durationLabels.indexOf(label) === -1) durationLabels.push(label);
    }
  }
  const durations = durationLabels.slice(0, 4).map((label, index) => ({ id: "d" + index, label, sub: "" }));

  return { services, locations, staff, durations };
}

export interface PublicCatalogRead {
  services: Dict[];
  /**
   * One already-normalised list. The SERVER resolves which source it came from
   * -- the journey's published branches when it has them, otherwise the
   * tenant's public locations -- and the client is deliberately not told which.
   * Knowing would change nothing that renders, and a public payload should not
   * widen to make a test easier to write.
   */
  locations: Dict[];
}

/**
 * The public catalogue for a slug: what a visitor may be offered, and where.
 *
 * Returns **null** when there was no catalogue to read — no slug, or a journey
 * that is missing or unpublished. That is deliberately distinct from a payload
 * whose arrays are empty, which means the tenant genuinely offers nothing. A
 * caller must be able to tell "nothing published" from "the read did not
 * happen", because only the first should replace what is already on screen.
 */
export async function loadPublicCatalog(slug: string, mode: AppointmentMode): Promise<PublicCatalogRead | null> {
  if (!slug) return null;
  const payload = await requestPublicSurface<Dict>(
    "/v1/journeys/public/" + encodeURIComponent(slug) + "/catalog" + staffQuery(mode),
    { method: "GET" },
    publicReadOptions(mode),
    true,
  );
  if (!payload) return null;
  const services = payload.services;
  const locations = payload.locations;
  return {
    services: Array.isArray(services) ? services : [],
    // An older worker omits `locations` entirely. That reads as "none
    // published" — never as an error, and never as a reason to substitute rows.
    locations: Array.isArray(locations) ? locations : [],
  };
}

export interface AvailabilityWindow {
  /** ISO date to the real slots the backend offered for it. Never generated. */
  byDay: Record<string, PublicSlot[]>;
  staff: { id: string; name: string }[];
  selectedStaffId: string | null;
  /** The key the window was loaded for, so a stale response can be discarded. */
  key: string;
}

export const EMPTY_AVAILABILITY: AvailabilityWindow = { byDay: {}, staff: [], selectedStaffId: null, key: "" };

function isoDate(d: Date): string {
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
}

const IANA_ZONE_ALIASES: Record<string, string> = {
  alaska: "America/Anchorage", hawaii: "Pacific/Honolulu", pacific: "America/Los_Angeles",
  mountain: "America/Denver", central: "America/Chicago", eastern: "America/New_York",
};

function zoneName(timeZone?: string): string | undefined {
  const value = String(timeZone || "").trim();
  return value ? (IANA_ZONE_ALIASES[value.toLowerCase()] || value) : undefined;
}

/** A civil date anchored to the selected store, never to the visitor browser. */
function isoDateInZone(d: Date, timeZone?: string): string {
  const zone = zoneName(timeZone);
  if (!zone) return isoDate(d);
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(d);
  const get = (type: string) => parts.find((part) => part.type === type)?.value || "00";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function isoAddDays(iso: string, days: number): string {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

export function availabilityKey(slug: string, serviceId: string, staffId: string, from: string, to: string): string {
  return [slug, serviceId, staffId || "", from, to].join("|");
}

/**
 * Real availability for one service over a date window.
 *
 * Each slot is carried through WHOLE -- startUtc, endUtc and staffId are the
 * backend's own and are submitted unchanged. A displayed time is never parsed
 * back into an instant: that is how a booking lands an hour out across a
 * daylight-saving boundary.
 */
/** One read serves at most this many days (the server's PUBLIC_AVAILABILITY_MAX_SPAN_DAYS). */
const MAX_SPAN_DAYS = 62;

export async function loadAvailability(params: {
  slug: string;
  serviceId: string;
  staffId?: string;
  /** The STORE whose opening hours the slots come from (Phase 1, 2026-09-14).
   *  Availability is store-level: a store's hours are the whole window, and
   *  every worker on its roster inherits them. The contract's query has no
   *  location dimension yet — CD-API-REQUEST-store-level-availability-2026-09-14.md
   *  — so this is sent as `locationId` and a server that does not know it
   *  ignores it, degrading to the service-wide window rather than failing. */
  locationId?: string;
  /** IANA zone (or the catalog's six-zone alias) for the selected store. */
  locationTimezone?: string;
  daysAhead?: number;
  /** Explicit civil window (JAI-2135: the caller's store-anchored dates). */
  dateFrom?: string;
  dateTo?: string;
}): Promise<AvailabilityWindow> {
  const slug = params.slug;
  const serviceId = params.serviceId;
  if (!slug || !serviceId) return EMPTY_AVAILABILITY;
  const span = Math.min(Math.max(1, params.daysAhead || 60), MAX_SPAN_DAYS - 1);
  const zone = zoneName(params.locationTimezone);
  // Never the browser's date. With neither an explicit dateFrom nor a store
  // zone, dateFrom is OMITTED and the server's venue-aware default anchors the
  // read; dateTo then ends `span` (at most 60) days after the UTC date, which is
  // within one civil day of any server today, so no read exceeds MAX_SPAN_DAYS.
  const from = String(params.dateFrom || "").trim() || (zone ? isoDateInZone(new Date(), zone) : "");
  let to = String(params.dateTo || "").trim()
    || (from ? isoAddDays(from, span) : isoAddDays(new Date().toISOString().slice(0, 10), Math.min(span, MAX_SPAN_DAYS - 2)));
  if (from && to > isoAddDays(from, MAX_SPAN_DAYS - 1)) to = isoAddDays(from, MAX_SPAN_DAYS - 1);
  // The store is part of the cache identity: two stores keep different hours
  // (Chicago 9-5, London 9-5 Europe/London) and therefore different slots.
  const key = availabilityKey(slug, serviceId, (params.staffId || "") + "|" + (params.locationId || ""), from || "server", to);

  const query: PublicJourneyAvailabilityQuery = { serviceId: serviceId, dateTo: to };
  if (from) query.dateFrom = from;
  if (params.staffId) query.staffId = params.staffId;
  if (params.locationId) query.locationId = params.locationId;
  /* The personal link's member (CC 0308 §3). This read only runs on the public page. */
  const linkStaff = personalLinkStaff("public");
  if (linkStaff) query.staff = linkStaff;
  const response = (await getPublicJourneyAvailability(slug, query)) as Dict;
  return normalizeAvailabilityResponse(response, key);
}

/**
 * The ONE projection of an availability response (`{ data: { days, staff,
 * selectedStaffId } }`) onto the wizard's window, shared by the public read and
 * the authoring preview so the two can never disagree about a slot. Every slot
 * keeps its producer's own id, start/end, staff, store and remaining capacity.
 */
export function normalizeAvailabilityResponse(response: unknown, key: string): AvailabilityWindow {
  const payload = ((response && (response as Dict).data) || {}) as Dict;
  const byDay: Record<string, PublicSlot[]> = {};
  const days: Dict[] = Array.isArray(payload.days) ? payload.days : [];
  for (const day of days) {
    const date = String(day && day.date ? day.date : "");
    if (!date) continue;
    const slots: Dict[] = Array.isArray(day.slots) ? day.slots : [];
    byDay[date] = slots
      .map((slot) => ({
        id: String(slot.id || slot.slotId || ""),
        time: String(slot.time || ""),
        startUtc: String(slot.startUtc || slot.start_utc || ""),
        endUtc: String(slot.endUtc || slot.end_utc || ""),
        staffId: String(slot.staffId || slot.staff_id || ""),
        staffName: slot.staffName != null ? slot.staffName : (slot.staff_name != null ? slot.staff_name : null),
        // Dual-cased like every other field on this DTO. Dropped here, the
        // store the visitor actually picked a time at could not reach the
        // submission.
        ...((slot.locationId || slot.location_id)
          ? { locationId: String(slot.locationId || slot.location_id) }
          : {}),
        // Retained only as the finite number the producer sent. Dropping it made
        // a full offer indistinguishable from an open one.
        ...(typeof slot.remaining === "number" && Number.isFinite(slot.remaining)
          ? { remaining: slot.remaining }
          : {}),
      }))
      .filter((slot) => slot.startUtc && slot.endUtc);
  }

  const staffRows: Dict[] = Array.isArray(payload.staff) ? payload.staff : [];
  return {
    byDay: byDay,
    staff: staffRows.filter((row) => row && row.id).map((row) => ({ id: String(row.id), name: String(row.name || "") })),
    selectedStaffId: payload.selectedStaffId ? String(payload.selectedStaffId) : null,
    key: key,
  };
}

/** The authoring preview's date horizon ceiling, in days after `dateFrom`. */
export const PREVIEW_MAX_HORIZON_DAYS = 92;

/**
 * Real availability for an UNSAVED slot interval, for the authoring preview only.
 *
 * Calls `previewAdminJourneyAvailability` for the selected admin journey (a draft
 * with no published version is fine): the real engine, with `slotInterval`
 * overridden for this request and nothing persisted. The public booking page never
 * calls this — it keeps `loadAvailability`, which sends no interval.
 *
 * A missing journey or an interval outside 1..1140 is REFUSED here (a rejected
 * promise), never answered from the public read: a preview of the published
 * config would show the author times their unsaved change does not produce.
 */
export async function loadAvailabilityPreview(params: {
  journeyId: string;
  serviceId: string;
  slotInterval: number;
  staffId?: string;
  locationId?: string;
  daysAhead?: number;
}): Promise<AvailabilityWindow> {
  const journeyId = String(params.journeyId || "").trim();
  if (!journeyId) throw new Error("There is no saved appointment journey to preview yet.");
  const serviceId = String(params.serviceId || "");
  if (!serviceId) return EMPTY_AVAILABILITY;
  const interval = params.slotInterval;
  if (typeof interval !== "number" || !Number.isInteger(interval) || interval < 1 || interval > 1140) {
    throw new Error("The slot interval must be a whole number of minutes from 1 to 1140.");
  }
  const today = new Date();
  const days = Math.min(Math.max(1, Math.round(params.daysAhead || 60)), PREVIEW_MAX_HORIZON_DAYS);
  const from = isoDate(today);
  const to = isoDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() + days));
  const key = ["preview", journeyId, serviceId, (params.staffId || "") + "|" + (params.locationId || ""), String(interval), from, to].join("|");
  const input: Dict = { serviceId, dateFrom: from, dateTo: to, slotInterval: interval };
  if (params.staffId) input.staffId = params.staffId;
  if (params.locationId) input.locationId = params.locationId;
  const response = await previewAdminJourneyAvailability(journeyId, input as any);
  return normalizeAvailabilityResponse(response, key);
}

/** Which part of the day a real slot belongs to, for the grouped slot picker. */
export function partOfDay(slot: PublicSlot): "Morning" | "Afternoon" | "Evening" {
  const match = /^(\d{1,2}):(\d{2})\s*(AM|PM)?/i.exec(slot.time || "");
  let hour: number;
  if (match) {
    hour = parseInt(match[1], 10);
    // The availability endpoint returns 24-hour times ("09:00", "14:00"). Only a
    // meridiem-bearing value may be folded onto a 12-hour clock; doing it
    // unconditionally put every afternoon slot in the Morning group.
    const meridiem = (match[3] || "").toLowerCase();
    if (meridiem) {
      hour = hour % 12;
      if (meridiem === "pm") hour += 12;
    }
  } else {
    const parsed = new Date(slot.startUtc);
    hour = Number.isNaN(parsed.getTime()) ? 12 : parsed.getHours();
  }
  if (hour < 12) return "Morning";
  if (hour < 17) return "Afternoon";
  return "Evening";
}

export interface BookingRequest {
  slug: string;
  serviceId: string;
  locationId?: string;
  /** How the appointment is held, when the customer is booked by one method of several
   *  (CC 0077 d). One of the service's channels; left out for a single-method service,
   *  whose method the door records itself. */
  method?: "inPerson" | "phone" | "video";
  slot: PublicSlot;
  guest?: { firstName?: string; lastName?: string; email: string; phone?: string; phoneCountry?: string };
  answers?: Record<string, unknown>;
  /** The details step's consent box, as the customer left it. */
  agreedToTerms: boolean;
  notificationPreference?: string;
  /** Present only after the one-time One Stop handoff has been redeemed. */
  bookingSessionCsrf?: string;
}

export interface PortalBookingSessionContext {
  displayName: string | null;
  profile: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
  };
  csrf: string;
}

/** Read the verified customer's profile through the host-only booking session. */
export async function loadPortalBookingSession(slug: string): Promise<PortalBookingSessionContext> {
  const { data: context } = await getPortalBookingCustomerContext(slug, publicReadOptions("public"));
  if (!context || !context.profile || !context.csrf) throw new Error("Your secure booking session is no longer available.");
  return context;
}

/**
 * Submit a real booking. PREVIEW NEVER CALLS THIS -- the designer's confirmation
 * step is a rendering of settings, and a preview that created a customer record
 * would be indistinguishable from a real one in the tenant's own data.
 */
export async function submitBooking(request: BookingRequest): Promise<Dict> {
  const slot = request.slot;
  const input: Dict = {
    serviceId: request.serviceId,
    // The slot's own staff identity, unchanged. The customer may never have
    // chosen a person, but the instant they picked belongs to one.
    staffId: slot.staffId,
    slotStartUtc: slot.startUtc,
    slotEndUtc: slot.endUtc,
    answers: request.answers || {},
    // (answers carry the details-step responses to the service's booking questions)
    agreedToTerms: request.agreedToTerms === true,
  };
  if (request.locationId) input.locationId = request.locationId;
  if (request.method) input.method = request.method;
  if (request.notificationPreference) input.notificationPreference = request.notificationPreference;
  if (request.bookingSessionCsrf) {
    const portalStaff = personalLinkStaff("public");
    const booked = await bookPortalJourneyAppointment(
      request.slug,
      input as any,
      request.bookingSessionCsrf,
      publicReadOptions("public"),
      portalStaff ? { staff: portalStaff } : undefined,
    );
    return booked.data;
  }
  if (!request.guest) throw new Error("Your details are required to book this appointment.");
  input.guest = request.guest;
  /* Where the booker came from (public-touch.ts). Anonymous lane only: the portal booking input has no attribution field. */
  const attribution = publicAttributionForSubmit();
  if (attribution) input.attribution = attribution;
  /* The personal link's member rides in the booking call's query (CC 0308 §3). */
  const linkStaff = personalLinkStaff("public");
  return (await bookPublicJourneyAppointment(request.slug, input as any, undefined, linkStaff ? { staff: linkStaff } : undefined)) as Dict;
}

/** Read a booking back, which is what proves it exists rather than 2xx alone. */
export async function readBooking(
  slug: string,
  bookingId: string,
  email: string,
  mode: AppointmentMode,
): Promise<Dict | null> {
  if (!slug || !bookingId || !email) return null;
  const path = "/v1/journeys/public/" + encodeURIComponent(slug)
    + "/bookings/" + encodeURIComponent(bookingId)
    + "?email=" + encodeURIComponent(email);
  return await requestPublicSurface<Dict>(path, { method: "GET" }, publicReadOptions(mode), true);
}
